import { css, fillOutlined, circle, shade } from './draw-utils';
import { blotches, glow, lumpPath, makeRock, makeTile, makeWallBlob, wrap } from './env-kit';
import type { ProceduralDef } from '../types';

/**
 * Theme kits S3 Brood Depths + S4 Anomaly Rim (docs/04 environment themes):
 * floor, wall mass, edge walls, cover rocks and the landmark.
 */
const BROOD = { floor: 0x22301e, cell: 0x283923, rim: 0x18231a, vein: 'rgba(150, 70, 130, 0.16)' };
const RIM = { floor: 0x1b1730, grid: 'rgba(98, 78, 170, 0.3)', star: 'rgba(230, 215, 255, 0.5)' };

// ---- S3 Brood Depths ----

export const envGroundBrood = makeTile('env/ground-brood', BROOD.floor, 31, (ctx, w, h, rnd) => {
  // Membrane cells: low-contrast fills, faint rims — texture, not noise
  for (let i = 0; i < 26; i++) {
    const x = rnd() * w;
    const y = rnd() * h;
    const r = 26 + rnd() * 34;
    wrap(w, h, (dx, dy) => {
      circle(ctx, x + dx, y + dy, r);
      ctx.fillStyle = css(BROOD.cell);
      ctx.globalAlpha = 0.55;
      ctx.fill();
      ctx.globalAlpha = 0.35;
      ctx.strokeStyle = css(BROOD.rim);
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.globalAlpha = 1;
    });
  }
  ctx.strokeStyle = BROOD.vein;
  ctx.lineWidth = 2.5;
  for (let i = 0; i < 5; i++) {
    const x = rnd() * w;
    const y = rnd() * h;
    const a = rnd() * Math.PI * 2;
    wrap(w, h, (dx, dy) => {
      ctx.beginPath();
      ctx.moveTo(x + dx, y + dy);
      ctx.quadraticCurveTo(
        x + dx + Math.cos(a + 0.8) * 60, y + dy + Math.sin(a + 0.8) * 60,
        x + dx + Math.cos(a) * 110, y + dy + Math.sin(a) * 110,
      );
      ctx.stroke();
    });
  }
}, 512);

export const envWallMassBrood = makeTile('env/wallmass-brood', 0x0f170c, 32, (ctx, w, h, rnd) => {
  blotches(ctx, w, h, rnd, 0x1a2715, 0.95, 12, 16, 38);
  blotches(ctx, w, h, rnd, 0x2a1a28, 0.5, 5, 10, 22);
});

export const envWallBrood = makeWallBlob('env/wall-brood', 0x3e5a32, 0x5a7c46, 0x8a4a78);
export const envRockBrood = makeRock('env/rock-brood', 'lump', 0x4d6e3c, 0x6c8f54, 0x26361e);

/** Egg mound: a clutch of glowing egg sacs on a fleshy base. */
export const envLandmarkBrood: ProceduralDef = {
  key: 'env/landmark-brood', width: 384, height: 384, anchorX: 0.5, anchorY: 0.5, frames: 1,
  draw(ctx, w) {
    const c = w / 2;
    const R = c - 16;
    lumpPath(ctx, c, c, R * 0.9, 14, 0.07, 0.4);
    fillOutlined(ctx, 0x3a4f2e, 9);
    lumpPath(ctx, c - R * 0.06, c - R * 0.06, R * 0.7, 12, 0.08, 1.1);
    ctx.fillStyle = css(0x4a6438);
    ctx.fill();
    const eggs: ReadonlyArray<readonly [number, number, number]> = [
      [0, 0, 0.3], [-0.42, -0.2, 0.22], [0.4, -0.3, 0.2], [0.35, 0.36, 0.23],
      [-0.32, 0.4, 0.2], [-0.05, -0.5, 0.17], [0.6, 0.06, 0.15], [-0.62, 0.12, 0.15],
    ];
    for (const [ex, ey, er] of eggs) {
      const x = c + ex * R;
      const y = c + ey * R;
      const r = er * R;
      ctx.beginPath();
      ctx.ellipse(x, y, r, r * 1.12, 0.3, 0, Math.PI * 2);
      fillOutlined(ctx, 0xb8d68a, 5);
      glow(ctx, x, y + r * 0.1, r * 0.75, '200, 255, 120', 0.7);
      circle(ctx, x - r * 0.35, y - r * 0.4, r * 0.22);
      ctx.fillStyle = 'rgba(255, 255, 230, 0.55)';
      ctx.fill();
    }
  },
};

// ---- S4 Anomaly Rim ----

export const envGroundRim = makeTile('env/ground-rim', RIM.floor, 41, (ctx, w, h, rnd) => {
  blotches(ctx, w, h, rnd, 0x221c3e, 0.45, 9, 30, 80);
  ctx.strokeStyle = RIM.grid;
  ctx.lineWidth = 1.5;
  for (let v = 0; v <= w; v += 64) {
    ctx.beginPath(); ctx.moveTo(v, 0); ctx.lineTo(v, h); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(0, v); ctx.lineTo(w, v); ctx.stroke();
  }
  // Glowing grid nodes + scattered stars
  ctx.fillStyle = 'rgba(176, 124, 255, 0.55)';
  for (let x = 0; x <= w; x += 128) {
    for (let y = 0; y <= h; y += 128) {
      circle(ctx, x, y, 3);
      ctx.fill();
    }
  }
  ctx.fillStyle = RIM.star;
  for (let i = 0; i < 40; i++) {
    circle(ctx, rnd() * w, rnd() * h, 0.8 + rnd() * 1.2);
    ctx.fill();
  }
}, 512);

export const envWallMassRim = makeTile('env/wallmass-rim', 0x0a0816, 42, (ctx, w, h, rnd) => {
  blotches(ctx, w, h, rnd, 0x141029, 0.9, 8, 18, 44);
  ctx.fillStyle = 'rgba(220, 200, 255, 0.5)';
  for (let i = 0; i < 18; i++) {
    circle(ctx, rnd() * w, rnd() * h, 0.6 + rnd() * 1.1);
    ctx.fill();
  }
});

export const envWallRim = makeWallBlob('env/wall-rim', 0x3c3266, 0x574a92, 0xc9a8ff);
export const envRockRim = makeRock('env/rock-rim', 'shard', 0x5b4aa0, 0x8b7ad0, 0xe6d6ff);

/** Monolith: faceted obelisk seen from above, rune ring + halo. */
export const envLandmarkRim: ProceduralDef = {
  key: 'env/landmark-rim', width: 384, height: 384, anchorX: 0.5, anchorY: 0.5, frames: 1,
  draw(ctx, w) {
    const c = w / 2;
    const R = c - 16;
    glow(ctx, c, c, R, '176, 124, 255', 0.35);
    ctx.lineWidth = 5;
    ctx.strokeStyle = 'rgba(200, 170, 255, 0.7)';
    ctx.beginPath();
    ctx.arc(c, c, R * 0.86, 0, Math.PI * 2);
    ctx.stroke();
    for (let i = 0; i < 12; i++) {
      const a = (Math.PI * 2 * i) / 12;
      ctx.save();
      ctx.translate(c + Math.cos(a) * R * 0.86, c + Math.sin(a) * R * 0.86);
      ctx.rotate(a);
      ctx.fillStyle = css(0xe6d6ff);
      ctx.fillRect(-3, -7, 6, 14);
      ctx.restore();
    }
    const pts: ReadonlyArray<readonly [number, number]> = [
      [0, -0.66], [0.42, -0.1], [0.3, 0.55], [-0.3, 0.55], [-0.42, -0.1],
    ];
    ctx.beginPath();
    pts.forEach(([x, y], i) => (i === 0 ? ctx.moveTo(c + x * R, c + y * R) : ctx.lineTo(c + x * R, c + y * R)));
    ctx.closePath();
    fillOutlined(ctx, 0x2e2552, 8);
    // Lit facets
    ctx.fillStyle = css(shade(0x2e2552, 1.7));
    ctx.beginPath();
    ctx.moveTo(c, c - 0.66 * R); ctx.lineTo(c - 0.42 * R, c - 0.1 * R); ctx.lineTo(c, c + 0.05 * R);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = css(shade(0x2e2552, 1.3));
    ctx.beginPath();
    ctx.moveTo(c - 0.42 * R, c - 0.1 * R); ctx.lineTo(c - 0.3 * R, c + 0.55 * R); ctx.lineTo(c, c + 0.05 * R);
    ctx.closePath();
    ctx.fill();
    glow(ctx, c, c + R * 0.05, R * 0.2, '220, 190, 255', 0.95);
  },
};
